import React, { useCallback, useEffect, useMemo, useState } from "react";
import { client } from "@/lib/api";
import { toast } from "sonner";
import { confirm, prompt } from "@/lib/dialog";

const STATE_LABELS = { free: "free", trialing: "trial", founding: "founding", paid: "paid", comped: "comped", expired: "expired" };
const STATE_ORDER = ["trialing", "founding", "paid", "comped", "expired", "free"];

export default function Users() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [foundingCap, setFoundingCap] = useState(1000);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    q: "", membership_state: "", role: "", joined_after: "", joined_before: "",
    trial_ending: "", sort: "joined_desc",
  });
  const [sheetOpen, setSheetOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
      p.set("page", String(page));
      p.set("page_size", "50");
      const r = await client.get(`/admin/users?${p.toString()}`);
      setRows(r.data.users || []);
      setCounts(r.data.counts || {});
      setTotal(r.data.total || 0);
      setFoundingCap(r.data.founding_cap || 1000);
    } finally { setLoading(false); }
  }, [filters, page]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const toggleAdmin = async (u) => {
    if (u.is_admin && !(await confirm(`revoke admin from ${u.email}?`))) return;
    try {
      const r = await client.post(`/admin/users/${u.user_id}/admin`, { grant: !u.is_admin });
      setRows((prev) => prev.map((x) => x.user_id === u.user_id ? { ...x, ...r.data.user } : x));
      toast.success(!u.is_admin ? "admin granted" : "admin revoked");
    } catch (e) { toast.error(e?.response?.data?.detail || "failed"); }
  };
  const grantComp = async (u) => {
    const note = (await prompt("comp note (optional):", "")) || "";
    const cap = await confirm("count toward the 1,000 founding cap?");
    try {
      await client.post(`/admin/users/${u.user_id}/grant-comp`, { counts_toward_cap: cap, note: note || "" });
      toast.success("comp granted"); load();
    } catch (e) { toast.error("failed"); }
  };
  const revokeMembership = async (u) => {
    if (!(await confirm(`revoke membership for ${u.email}?`))) return;
    try {
      await client.post(`/admin/users/${u.user_id}/revoke-membership`);
      toast.success("revoked"); load();
    } catch { toast.error("failed"); }
  };

  const setFilter = (k, v) => { setPage(1); setFilters((f) => ({ ...f, [k]: v })); };

  const foundingLine = `founding ${counts.founding_taken || 0} of ${foundingCap}`;

  const FilterControls = (
    <div className="grid gap-3">
      <label className="text-[11px] uppercase tracking-[0.14em] text-neutral-500">membership</label>
      <select value={filters.membership_state} onChange={(e) => setFilter("membership_state", e.target.value)}
              data-testid="users-filter-state"
              className="rounded-md border border-neutral-300 p-2 text-sm">
        <option value="">any</option>
        <option value="free">free</option>
        <option value="trial">trial</option>
        <option value="founding">founding (KES 200 for life)</option>
        <option value="paid">paid</option>
        <option value="comped">comped</option>
        <option value="expired">expired</option>
      </select>
      <label className="text-[11px] uppercase tracking-[0.14em] text-neutral-500">role</label>
      <select value={filters.role} onChange={(e) => setFilter("role", e.target.value)}
              data-testid="users-filter-role"
              className="rounded-md border border-neutral-300 p-2 text-sm">
        <option value="">any</option>
        <option value="member">member</option>
        <option value="staff">staff</option>
        <option value="admin">admin</option>
        <option value="organiser">curator</option>
      </select>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[11px] uppercase tracking-[0.14em] text-neutral-500">joined after</label>
          <input type="date" value={filters.joined_after} onChange={(e) => setFilter("joined_after", e.target.value)}
                 data-testid="users-filter-after"
                 className="w-full rounded-md border border-neutral-300 p-2 text-sm" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-[0.14em] text-neutral-500">joined before</label>
          <input type="date" value={filters.joined_before} onChange={(e) => setFilter("joined_before", e.target.value)}
                 data-testid="users-filter-before"
                 className="w-full rounded-md border border-neutral-300 p-2 text-sm" />
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={filters.trial_ending === "3d"}
               data-testid="users-filter-trial-ending"
               onChange={(e) => setFilter("trial_ending", e.target.checked ? "3d" : "")} />
        trial ending in the next 3 days
      </label>
      <label className="text-[11px] uppercase tracking-[0.14em] text-neutral-500">sort</label>
      <select value={filters.sort} onChange={(e) => setFilter("sort", e.target.value)}
              data-testid="users-filter-sort"
              className="rounded-md border border-neutral-300 p-2 text-sm">
        <option value="joined_desc">newest first</option>
        <option value="joined_asc">oldest first</option>
        <option value="name_asc">name a → z</option>
        <option value="trial_end_asc">trial end soonest</option>
      </select>
    </div>
  );

  return (
    <div className="p-6" data-testid="admin-users">
      <p className="text-[11px] uppercase tracking-[0.22em] text-neutral-500">watu wako · admin</p>
      <h1 className="mt-1 lowercase" style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.025em", fontSize: 30 }}>
        members and roles
      </h1>

      {/* Count strip */}
      <div className="mt-4 flex flex-wrap gap-2" data-testid="users-count-strip">
        <Chip label={foundingLine} strong />
        {STATE_ORDER.map((s) => (
          <Chip key={s} label={`${STATE_LABELS[s]} ${(s === "founding" ? counts.founding_taken : counts[s]) || 0}`}
                active={filters.membership_state === (s === "trialing" ? "trial" : s)}
                onClick={() => setFilter("membership_state", filters.membership_state === (s === "trialing" ? "trial" : s) ? "" : (s === "trialing" ? "trial" : s))} />
        ))}
        <Chip label={`admin ${counts.admin || 0}`} active={filters.role === "admin"}
              onClick={() => setFilter("role", filters.role === "admin" ? "" : "admin")} />
      </div>

      {/* Search + filters */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <input value={filters.q} onChange={(e) => setFilter("q", e.target.value)}
               data-testid="users-search"
               placeholder="search name, email, phone, @handle"
               className="w-full max-w-md rounded-md border border-neutral-300 p-2 text-sm sm:w-96" />
        <button onClick={() => setSheetOpen(true)}
                data-testid="users-open-filters"
                className="rounded-md border border-neutral-300 px-3 py-2 text-sm md:hidden">
          filters
        </button>
        <div className="hidden md:flex md:items-end md:gap-3">
          {FilterControls}
        </div>
      </div>

      {/* Table */}
      <div className="mt-6 overflow-x-auto rounded-lg border" style={{ borderColor: "#e5e5e5", background: "#FFFFFF" }}>
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left">
            <tr>
              <th className="p-3">Member</th>
              <th className="p-3">State</th>
              <th className="p-3">Trial</th>
              <th className="p-3">Joined</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="p-8 text-center text-neutral-500">loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-neutral-500">no members match this filter yet.</td></tr>
            )}
            {rows.map((u) => (
              <tr key={u.user_id} className="border-t border-neutral-100" data-testid={`users-row-${u.user_id}`}>
                <td className="p-3">
                  <div className="font-semibold">{u.display_name || u.name || u.email || "—"}</div>
                  <div className="text-xs text-neutral-500">{u.email}</div>
                  {u.handle && <div className="text-xs text-neutral-500">@{u.handle}</div>}
                </td>
                <td className="p-3">
                  <StateBadge state={u.membership_state} is_founding={u.is_founding} is_admin={u.is_admin} />
                </td>
                <td className="p-3 text-xs text-neutral-600">
                  {u.membership_state === "trialing"
                    ? (u.trial_days_left != null ? `${u.trial_days_left} day${u.trial_days_left === 1 ? "" : "s"} left` : "—")
                    : "—"}
                </td>
                <td className="p-3 text-xs text-neutral-500">{(u.created_at || "").slice(0, 10)}</td>
                <td className="p-3 text-right">
                  <button className="mr-2 rounded-md border border-neutral-300 px-2 py-1 text-xs"
                          data-testid={`users-comp-${u.user_id}`} onClick={() => grantComp(u)}>comp</button>
                  {["trialing", "paid", "comped", "founding"].includes(u.membership_state) && (
                    <button className="mr-2 rounded-md border border-neutral-300 px-2 py-1 text-xs"
                            data-testid={`users-revoke-${u.user_id}`} onClick={() => revokeMembership(u)}>revoke</button>
                  )}
                  <button className="rounded-md border border-neutral-300 px-2 py-1 text-xs"
                          data-testid={`users-admin-${u.user_id}`} onClick={() => toggleAdmin(u)}>
                    {u.is_admin ? "admin" : "make admin"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between">
        <p className="text-xs text-neutral-500">page {page} · {total} match{total === 1 ? "" : "es"}</p>
        <div className="flex gap-2">
          <button className="rounded-md border border-neutral-300 px-3 py-1 text-xs" disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}>prev</button>
          <button className="rounded-md border border-neutral-300 px-3 py-1 text-xs" disabled={rows.length < 50}
                  onClick={() => setPage((p) => p + 1)}>next</button>
        </div>
      </div>

      {/* Phone bottom sheet */}
      {sheetOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 60 }}
             onMouseDown={(e) => { if (e.target === e.currentTarget) setSheetOpen(false); }}>
          <div style={{ position: "absolute", left: 0, right: 0, bottom: 0,
                         background: "#FFFFFF", borderTopLeftRadius: 16, borderTopRightRadius: 16,
                         padding: 20, maxHeight: "85vh", overflowY: "auto" }}
               data-testid="users-filter-sheet">
            <div className="mb-4 flex items-center justify-between">
              <p style={{ fontFamily: "Poppins", fontWeight: 800, textTransform: "lowercase" }}>filters</p>
              <button onClick={() => setSheetOpen(false)} className="text-2xl leading-none">×</button>
            </div>
            {FilterControls}
            <button onClick={() => setSheetOpen(false)}
                    className="mt-4 w-full rounded-md py-3 text-sm font-semibold"
                    style={{ background: "#FFD60A", color: "#1A1A30" }}>
              apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Chip({ label, active, onClick, strong }) {
  return (
    <button onClick={onClick}
            className="rounded-full border px-3 py-1 text-xs font-semibold lowercase"
            style={{
              background: active ? "#1A1A30" : (strong ? "#FFD60A" : "#FFFFFF"),
              color: active ? "#FFFFFF" : "#1A1A30",
              borderColor: active ? "#1A1A30" : (strong ? "#FFD60A" : "#e5e5e5"),
              cursor: onClick ? "pointer" : "default",
            }}>
      {label}
    </button>
  );
}

function StateBadge({ state, is_founding, is_admin }) {
  const bg = state === "trialing" ? "#FFD60A" : state === "founding" ? "#FFD60A" : state === "paid" ? "#44DD88" : state === "comped" ? "#9D4EDD" : state === "expired" ? "#F88" : "#e5e5e5";
  const fg = state === "expired" ? "#7A1F1F" : (state === "comped" ? "#FFFFFF" : "#1A1A30");
  const label = STATE_LABELS[state] || state;
  return (
    <div className="flex items-center gap-1 lowercase">
      <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
            style={{ background: bg, color: fg }}>{label}</span>
      {is_founding && <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: "#1A1A30", color: "#FFD60A" }}>founding</span>}
      {is_admin && <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: "#1A1A30", color: "#FFFFFF" }}>admin</span>}
    </div>
  );
}
